import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { RevokeSessionResult, SessionStaff, StaffSession } from './types';

const ownKey = ['staff-sessions', 'own'] as const;
const staffKey = (staffId: string) =>
  ['staff-sessions', 'staff', staffId] as const;

export function useOwnSessions(enabled = true) {
  return useQuery({
    queryKey: ownKey,
    queryFn: ({ signal }) =>
      api
        .get<StaffSession[]>('/auth/sessions', { signal })
        .then((response) => response.data),
    enabled,
  });
}

export function useSessionStaff(enabled = true) {
  return useQuery({
    queryKey: ['staff-sessions', 'directory'],
    queryFn: ({ signal }) =>
      api
        .get<SessionStaff[]>('/admin/staff', { signal })
        .then((response) => response.data),
    enabled,
  });
}

export function useStaffSessions(staffId: string, enabled = true) {
  return useQuery({
    queryKey: staffKey(staffId),
    queryFn: ({ signal }) =>
      api
        .get<StaffSession[]>(`/admin/staff/${staffId}/sessions`, { signal })
        .then((response) => response.data),
    enabled: enabled && Boolean(staffId),
  });
}

export function useRevokeOwnSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api
        .post<RevokeSessionResult>(`/auth/sessions/${sessionId}/revoke`)
        .then((response) => response.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ownKey }),
  });
}

export function useRevokeStaffSession(staffId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      reason,
    }: {
      sessionId: string;
      reason: string;
    }) =>
      api
        .post<RevokeSessionResult>(
          `/admin/staff/${staffId}/sessions/${sessionId}/revoke`,
          { reason },
        )
        .then((response) => response.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: staffKey(staffId) }),
  });
}

export function useRevokeAllStaffSessions(staffId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api
        .post<RevokeSessionResult>(
          `/admin/staff/${staffId}/sessions/revoke-all`,
          { reason },
        )
        .then((response) => response.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: staffKey(staffId) }),
  });
}
